<template>
  <div
    class="studio-text-editor"
    :class="{
      'studio-text-editor--placeholder-styles': showPlaceholderStyles,
      'studio-text-editor--word-tracking-nebula': wordTracking,
      'studio-text-editor--word-tracking-mystery': wordTracking && wordTrackingHasMysteryButton,
    }"
  >
    <EditorMenu
      v-if="toolbarEnabled && !disabled && (!floatingMenu || menuVisible)"
      ref="editorMenu"
      :append-to="getBeforeModuleContainerRef(id)"
      :module-id="id"
      :scale="scale"
      :trigger-element="$el"
      :trigger-additional-element-ids="parentModuleCanRotate ? [`rotate-module-button-${id}`] : []"
      :is-floating="floatingMenu"
      :box-name="canvasName === 'notes' ? 'notes' : 'editor'"
      :positions="['top']"
      :in-zoom-container="canvasName !== 'notes'"
      :update-menu-position-delay="25"
      :class="{ 'studio-text-editor__editor-menu--not-floating': !floatingMenu }"
      id-prefix="text-editor-floating-menu"
      @vue:mounted="editorMenuRef = $refs.editorMenu"
      @position-changed="editorMenuScale = $event.menuScale"
    >
      <TextEditorToolbar
        ref="textEditorToolbar"
        :text-editor="textEditor"
        tooltip-position="block-end"
        :disabled="disabled"
        class="studio-text-editor__toolbar"
        :class="{
          'studio-text-editor__toolbar--floating': floatingMenu,
          'studio-text-editor__toolbar--embedded': !floatingMenu,
        }"
        v-slot="props"
      >
        <TextEditorUndo
          v-if="extensionsSet.has('history')"
          v-bind="props"
        />
        <TextEditorRedo
          v-if="extensionsSet.has('history')"
          v-bind="props"
        />
        <TextEditorFontFamily
          v-if="extensionsSet.has('paragraph') && !wordTracking"
          v-bind="props"
          :font-family-filter="fontFamilyFilter"
        />
        <TextEditorFontSize
          v-if="extensionsSet.has('paragraph')"
          v-bind="props"
        />
        <TextEditorBold
          v-if="extensionsSet.has('bold')"
          v-bind="props"
        />
        <TextEditorItalic
          v-if="extensionsSet.has('italic')"
          v-bind="props"
        />
        <TextEditorStrike
          v-if="extensionsSet.has('strike')"
          v-bind="props"
        />
        <TextEditorUnderline
          v-if="extensionsSet.has('underline')"
          v-bind="props"
        />
        <TextEditorTextColor
          v-if="showTextColorOptions"
          v-bind="props"
          :offset-ref="toolbarOffsetRef"
          :scale="editorMenuScale"
          :colors="textColors"
          :show-search="false"
        />
        <TextEditorBulletList
          v-if="extensionsSet.has('bulletList')"
          v-bind="props"
        />
        <TextEditorOrderedList
          v-if="extensionsSet.has('orderedList')"
          v-bind="props"
        />
        <TextEditorSubscript
          v-if="extensionsSet.has('subscript')"
          v-bind="props"
        />
        <TextEditorSuperscript
          v-if="extensionsSet.has('superscript')"
          v-bind="props"
        />
        <TextEditorLink
          v-if="extensionsSet.has('link') && extensionsSet.has('button')"
          v-bind="props"
          :options="linkOptions"
        />
        <TextEditorAlign
          v-if="extensionsSet.has('paragraph') && !wordTracking"
          v-bind="props"
        />
        <TextEditorIcons
          v-if="extensionsSet.has('nebulaIconNode')"
          v-bind="props"
          dropdown-position="end"
          :offset-ref="toolbarOffsetRef"
          :scale="editorMenuScale"
        />
        <TextEditorTables
          v-if="extensionsSet.has('table')"
          v-bind="props"
        />
      </TextEditorToolbar>
    </EditorMenu>
    <TextEditor
      ref="textEditor"
      :aria-label="ariaLabel"
      :content="editorContent"
      :content-type="editorContentType"
      :extensions="extensions"
      :style="backgroundStyle"
      :theme="theme"
      :theme-config="themeConfig"
      :is-editable="isEditable"
      :de-links-domain="deLinksDomain"
      :open-links-while-editing="!disableLink"
      :overflow-y="editorOverflow"
      :watch-for-content-change="watchContent && mode === 'EDIT'"
      class="studio-text-editor__editor"
      :class="{
        'studio-text-editor__editor--rounded': theme || backgroundStyle,
        'studio-text-editor__editor--notes-edit-autosize': canvasName === 'notes'
          && mode === 'EDIT' && parentModuleAutosizeEnabled,
        'component-module-wrapper__module-contents--shape-mask': clipContents,
      }"
      @init="initEditor"
      @click="onEditorClick"
      @content-updated="onContentUpdated"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
    />
    <div
      v-if="wordTracking"
      class="studio-text-editor__word-tracking-toolbar"
    >
      <WordTrackingButton
        :disabled="!wordTrackingEnabled"
        :word-tracking-active="wordTrackingActive"
        :word-tracking="wordTracking"
        :is-mystery-button="wordTrackingHasMysteryButton"
        :theme="theme"
        @click="textEditor && textEditor.trackNextWord()"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import textileJS from 'textile-js';
import MarkdownIt from 'markdown-it';
import {
  get,
  trim,
} from 'lodash-es';
import {
  // Components
  TextEditor,
  TextEditorToolbar,
  TextEditorAlign,
  TextEditorFontFamily,
  TextEditorFontSize,
  TextEditorIcons,
  TextEditorUndo,
  TextEditorRedo,
  TextEditorBold,
  TextEditorItalic,
  TextEditorStrike,
  TextEditorSubscript,
  TextEditorSuperscript,
  TextEditorUnderline,
  TextEditorLink,
  TextEditorBulletList,
  TextEditorOrderedList,
  TextEditorTextColor,
  TextEditorTables,
  // Extensions
  History,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  Placeholder,
  Text,
  ListItem,
  HardBreak,
  Gapcursor,
  Doc,
  Paragraph,
  BulletList,
  OrderedList,
  NebulaIconNode,
  Alignment,
  Bold,
  Italic,
  Strike,
  Underline,
  Subscript,
  Superscript,
  Link,
  Button,
  TextColor,
  WordTracking,
  Typography,
  // Utils
  convertDocToV1,
} from '@discoveryedu/titan-text-editor';
import EditorMenu from '@/components/EditorMenu.vue';
import WordTrackingButton from '@/components/WordTrackingButton.vue';
import { bus } from '@/lib/eventBus';
import { stripTags, formatPageTitle } from '@/lib/utils';
import navigation from '@/mixins/navigation';
import { wordTrackingDefault, getThemeDefaults } from '@/lib/utils/store';
import i18next from '@/lib/i18next';
import {
  useEditorStore,
  useUserStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'StudioTextEditor',
  components: {
    TextEditor,
    TextEditorToolbar,
    TextEditorAlign,
    TextEditorFontFamily,
    TextEditorFontSize,
    TextEditorIcons,
    TextEditorUndo,
    TextEditorRedo,
    TextEditorBold,
    TextEditorItalic,
    TextEditorStrike,
    TextEditorSubscript,
    TextEditorSuperscript,
    TextEditorUnderline,
    TextEditorLink,
    TextEditorBulletList,
    TextEditorOrderedList,
    TextEditorTextColor,
    TextEditorTables,
    EditorMenu,
    WordTrackingButton,
  },
  mixins: [navigation],
  props: {
    backgroundStyle: {
      type: Object,
      default() {
        return null;
      },
    },
    content: {
      type: String,
      default: null,
    },
    dataType: {
      type: String,
      default: null,
    },
    theme: {
      type: Object,
      default: null,
    },
    themeConfig: {
      type: Object,
      default: null,
    },
    canvasName: {
      type: String,
      default: null,
    },
    clipContents: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    moduleActive: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    floatingMenu: {
      type: Boolean,
      defalut: false,
    },
    scale: {
      type: Number,
      default: 1,
    },
    textPlaceholder: {
      type: String,
      default: '',
    },
    defaultTextPlaceholder: {
      type: String,
      default: null,
    },
    overflow: {
      type: String,
      default: 'hidden',
    },
    watchContent: {
      type: Boolean,
      default: false,
    },
    getBeforeModuleContainerRef: {
      type: Function,
      default: () => null,
    },
    wordTracking: {
      type: Object,
      default: null,
    },
    parentModuleCanRotate: {
      type: Boolean,
      default: false,
    },
    parentModuleAutosizeEnabled: {
      type: Boolean,
      default: false,
    },
    showEmptyStyles: {
      type: Boolean,
      default: true,
    },
    toolbarEnabled: {
      type: Boolean,
      default: true,
    },
    ariaLabel: {
      type: String,
      default: undefined,
    },
    disableModalLinkOption: {
      type: Boolean,
      default: false,
    },
    disableLink: {
      type: Boolean,
      default: false,
    },
    disableTextColor: {
      type: Boolean,
      default: false,
    },
    disableIcons: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Object,
      default: null,
    },
  },
  emits: [
    'blur',
    'focus',
    'update',
  ],
  data() {
    return {
      textEditor: null,
      menuVisible: false,
      editorMenuRef: null,
      editorMenuScale: 1,
      textColors: [{
        id: 'sample-response',
        name: i18next.t('Sample Response'),
      }],
    };
  },
  mounted() {
    bus.on('document:mousedown', this.onBodyClick);
    bus.on('module:updated', this.onModuleUpdated);
  },
  beforeUnmount() {
    bus.off('document:mousedown', this.onBodyClick);
    bus.off('module:updated', this.onModuleUpdated);
  },
  watch: {
    mode() {
      if (this.textEditor) {
        this.textEditor.resetWordTracking();
      }
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'draftType',
      'modalCanvases',
      'pagesSorted',
      'currentPageId',
      'domains',
      'editorViewModePreview',
    ]),
    ...mapState(useUserStore, [
      'isDeUser',
    ]),
    isEditable() {
      return this.mode === 'EDIT' && !this.disabled;
    },
    deLinksDomain() {
      const { appDomain } = this.domains;
      if (
        this.mode !== 'VIEW'
        || this.isEditable
        || !appDomain
      ) return null;
      return appDomain;
    },
    extensions() {
      const themeDefaults = getThemeDefaults(this.theme?.id);
      /*
        Extensions are registered in 'priority' order. A default 'priority' has
        been assigned to extensions in titan-text-editor to be backwards compatable
        with Studio, but they are also listed here in order just in case.
      */
      const extensions = [
        Placeholder.configure({
          emptyNodeClass: 'titan-text-editor__empty-node',
          placeholder: this.placeholderContent,
          showOnlyWhenEditable: true,
          showOnlyCurrent: true,
        }),
        HardBreak,
        Doc,
        Text,
        Paragraph.configure(this.wordTracking
          ? { defaultClasses: wordTrackingDefault(themeDefaults.wordTrackingFont) }
          : undefined),
        Gapcursor,
        History,
        Typography.configure({
          oneHalf: false,
          oneQuarter: false,
          threeQuarters: false,
        }),
      ];

      if (!this.toolbarEnabled) return extensions;

      if (this.wordTracking) {
        extensions.push(WordTracking);
        return extensions;
      }

      extensions.push(...[
        Bold,
        BulletList,
        Italic,
      ]);

      if (!this.disableLink) {
        extensions.push(Button);
      }

      extensions.push(...[
        ListItem,
        OrderedList,
        Strike,
        Subscript,
        Superscript,
        Underline,
      ]);

      if (!this.disableTextColor) {
        extensions.push(TextColor.configure({
          colors: this.textColors.map((c) => c.id),
        }));
      }

      extensions.push(...[
        Alignment,
        Table.configure({
          resizable: true,
        }),
        TableHeader,
        TableCell,
        TableRow,
      ]);

      if (!this.disableLink) {
        extensions.push(Link);
      }

      if (!this.disableIcons) {
        extensions.push(NebulaIconNode.configure({
          nebulaSVGPath: this.$nebulaSVGPath,
        }));
      }

      return extensions;
    },
    extensionsSet() {
      return new Set(...[this.extensions.map((ext) => ext.name)]);
    },
    emptyContent() {
      // For templating modules, the empty content is the placeholder stripped of any content.
      // Then the user can immediately start typing with the same style as the placeholder.
      // The regex strips anything between the opening and closing tag
      return this.textPlaceholder
        ? textileJS(this.textPlaceholder).replace(/(<.*?>)(.*)(<\/.*?>)/, '$1$3')
        : '<p></p>';
    },
    placeholderContent() {
      // For templating modules, display the plaintext placeholder content
      return this.textPlaceholder
        ? stripTags(textileJS(this.textPlaceholder))
        : this.defaultTextPlaceholder || this.$t('Start typing…');
    },
    editorContent() {
      if (!this.content) {
        return this.emptyContent;
      }

      // Backwards compatibility for markdown.
      if (this.dataType === 'markdown') {
        const md = new MarkdownIt();
        const markdownToHtml = md.render(this.content);
        return markdownToHtml;
      }

      if (this.dataType === 'html') {
        return this.content;
      }

      // Backwards compatibility for textile.
      if (this.dataType === 'textile') {
        let textileString = this.content;

        // Decode ampersands from api then replace new lines in initial content
        textileString = textileString.replace(/&amp;/g, '&');
        textileString = textileString.replace(/&nl/g, '<p></p>');

        // Sizing backwards compatibility for textile
        textileString = textileString.replace(/h1\./g, 'p(size-xxl).');
        textileString = textileString.replace(/h2\./g, 'p(size-xl).');
        textileString = textileString.replace(/h3\./g, 'p(size-l).');
        textileString = textileString.replace(/h4\./g, 'p(size-m).');
        textileString = textileString.replace(/h5\./g, 'p(size-s).');
        textileString = textileString.replace(/h6\./g, 'p(size-xs).');

        // Pads substrings and superstrings with spaces so textile
        // parser recognizes sub/super elements
        textileString = textileString.replace(/\^.*?\^/g, (match) => ` ${match} `);
        textileString = textileString.replace(/~.*?~/g, (match) => ` ${match} `);

        // Encodes parenthesis in links so that they are not parsed by textile as title attrs
        textileString = textileString.replace(/".*?":/g, (match) => {
          let parsedMatch = match.replace(/\(/g, '&#40;');
          parsedMatch = parsedMatch.replace(/\)/g, '&#41;');
          return parsedMatch;
        });

        let htmlString = textileJS(textileString);

        // Trim sub/superstring padding to that it renders properly in editor
        htmlString = htmlString.replace(/\s<sup>.*?<\/sup>\s/g, (match) => trim(match));
        htmlString = htmlString.replace(/\s<sub>.*?<\/sub>\s/g, (match) => trim(match));

        // Decoding all encoded brakets so they properly render in the editor
        htmlString = htmlString.replace(/&lt;/g, '<');
        htmlString = htmlString.replace(/&gt;/g, '>');

        // Decode parenthesis in links
        htmlString = htmlString.replace(/&#40;/g, '(');
        htmlString = htmlString.replace(/&#41;/g, ')');

        return htmlString;
      }

      if (this.dataType === 'orion') {
        let htmlString = this.content;
        // Decoding all encoded brakets so they properly render in the editor
        htmlString = htmlString.replace(/&lt;/g, '<');
        htmlString = htmlString.replace(/&gt;/g, '>');

        return htmlString;
      }

      /*
        Assume dataType is 'json'.
        Convert document to version 1 format if necessary.
      */
      const doc = JSON.parse(this.content);
      const docVersion = doc?.attrs?.version || 0;
      return docVersion < 1 ? convertDocToV1(doc) : doc;
    },
    editorContentType() {
      const isJSON = !this.dataType || this.dataType === 'json';
      return isJSON ? 'json' : 'html';
    },
    editorOverflow() {
      if (this.overflow === 'overflow') return 'visible';
      if (this.overflow === 'scroll') return 'auto';
      return this.overflow;
    },
    toolbarOffsetRef() {
      return this.canvasName !== 'notes' && this.floatingMenu
        ? this.editorMenuRef
        : undefined;
    },
    formattedPages() {
      return this.pagesSorted.map((page, index) => ({
        ...page,
        formattedTitle: formatPageTitle(page, index, this.draftType),
      }));
    },
    showPlaceholderStyles() {
      return this.textEditor?.isEmpty && !this.moduleActive
        && this.isEditable && this.showEmptyStyles;
    },
    showTextColorOptions() {
      return this.isDeUser
        && !this.wordTracking
        && !this.disableTextColor
        && this.extensionsSet.has('textColor')
        && ['k-2-science-earth', 'k-2-science-life', 'k-2-science-physical']
          .includes(this.theme?.id);
    },
    linkOptions() {
      const options = [
        {
          markType: 'link',
          value: 'link',
          text: this.$t('URL'),
          target: {
            inputType: 'text',
            placeholder: 'https://',
            button: {
              text: this.$t('Apply'),
            },
          },
        },
        {
          markType: 'button',
          value: 'page',
          text: this.draftType === 'board'
            ? this.$t('Page')
            : this.$t('Slide'),
          target: {
            inputType: 'dropdown',
            groups: [
              {
                options: [
                  {
                    text: this.draftType === 'board'
                      ? this.$t('Previous page')
                      : this.$t('Previous slide'),
                    value: 'PREV',
                  },
                  {
                    text: this.draftType === 'board'
                      ? this.$t('Next page')
                      : this.$t('Next slide'),
                    value: 'NEXT',
                  },
                ],
              },
              {
                options: this.formattedPages.map((page) => ({
                  text: page.formattedTitle,
                  value: page.id,
                  disabled: page.id === this.currentPageId,
                })),
              },
            ],
          },
        },
      ];
      if (this.canvasName !== 'modalModules' && !this.disableModalLinkOption) {
        options.push({
          markType: 'button',
          value: 'window',
          text: this.$t('Window'),
          target: {
            inputType: 'button',
            value: this.id, // module id
            button: {
              text: this.$t('Customize'),
              click: this.openModalCanvas,
            },
          },
        });
      }
      return options;
    },
    wordTrackingActive() {
      return !!this.textEditor?.wordTrackingActive;
    },
    wordTrackingHasMysteryButton() {
      const page = this.page || this.currentPage;
      return get(this, 'wordTracking.button_style') === 'mystery-science'
        && get(page, 'options.theme.name') === 'mystery-science';
    },
    wordTrackingEnabled() {
      return !this.disabled
        && this.wordTracking
        && this.mode === 'VIEW'
        && !!this.textEditor?.wordTrackingTrackedWords?.length;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_MODAL_CANVAS,
      types.UPDATE_CURRENTLY_EDITING,
      types.VIEW_MODAL_CANVAS_BY_ID,
      types.VIEW_NEXT_PAGE,
      types.VIEW_PAGE_BY_ID,
      types.VIEW_PREV_PAGE,
    ]),
    initEditor(textEditor) {
      // set the text editor
      this.textEditor = textEditor;

      // in view mode, remove tiptap event listeners to avoid conflicts with highlight tool
      // this needs to be done here, as eventlisteners are still applied
      // even when the editor is disabled
      if (this.mode === 'VIEW' && textEditor.editor?.view && !this.editorViewModePreview) {
        this.$nextTick(() => {
          try {
            const { view } = textEditor.editor;
            view.domObserver.stop();
            const eventTypes = Object.keys(view.input.eventHandlers);
            eventTypes.forEach((type) => {
              view.dom.removeEventListener(type, view.input.eventHandlers[type]);
            });

            // add in the original click listener
            this.$refs.textEditor.$el.addEventListener('click', this.defaultEditorClick, false);
          } catch (e) {
            // do nothing here if theres an error removing event listeners
          }
        });
      }
    },
    onContentUpdated({ json, html }) {
      // Wrap TEA html so that we can use its class selector in core styles
      const studioTeiWrapper = document.createElement('div');
      studioTeiWrapper.classList.add('studio-tei-answer-wrapper');
      studioTeiWrapper.innerHTML = html;

      this.$emit('update', {
        html: studioTeiWrapper.outerHTML,
        json: JSON.stringify(json),
        doc: json,
      });
    },
    getContentHeight() {
      const textEditor = this.$refs?.textEditor;
      if (!textEditor) return null;
      let contentHeight = textEditor.getContentHeight();
      if (this.wordTracking) {
        contentHeight += 86; // Height of word tracking toolbar.
      }
      return contentHeight;
    },
    onBodyClick({ target }) {
      if (!this.isEditable) return;
      if (!this.floatingMenu) return;

      if (
        this.$refs?.textEditor?.$el?.contains?.(target)
          || this.$refs?.textEditorToolbar?.$el?.contains?.(target)
      ) {
        this.menuVisible = true;
      } else {
        this.menuVisible = false;
      }
    },
    openModalCanvas() {
      // Close this module's mini-menu
      this[types.UPDATE_CURRENTLY_EDITING]({
        [this.id]: null,
      });

      const existingModal = this.modalCanvases
        .find((modal) => modal.id === this.id);

      // If the modal doesn't already exist, create it
      if (!existingModal) {
        this[types.ADD_MODAL_CANVAS]({ id: this.id });
      }

      // Open the modal canvas
      this[types.VIEW_MODAL_CANVAS_BY_ID](this.id);
    },
    onEditorClick({ event, button, link }) {
      if (this.disableLink) return;
      if (link) {
        this.handleViewModeAnchorClick(event);
      }
      if (button) {
        this.handleButtonClick(button);
      }
    },
    defaultEditorClick(event) {
      let button = null;
      let link = null;

      // grab type and build out the dataset accordingly
      if (event.target.nodeName === 'BUTTON') {
        button = {
          targetPage: event.target.getAttribute('data-target-page'),
          action: event.target.getAttribute('data-action'),
        };
      } else if (event.target.nodeName === 'A') {
        link = true;
      }

      this.onEditorClick({ event, button, link });
    },
    handleButtonClick({ action, targetPage }) {
      if (!action || !targetPage) return;
      if (action === 'page') {
        if (targetPage === 'PREV') {
          this[types.VIEW_PREV_PAGE]();
        } else if (targetPage === 'NEXT') {
          this[types.VIEW_NEXT_PAGE]();
        } else {
          this[types.VIEW_PAGE_BY_ID](targetPage);
        }
      } else if (action === 'window') {
        this[types.VIEW_MODAL_CANVAS_BY_ID](targetPage);
      }
    },
    onModuleUpdated({ updatedModule, isLocalUndoRedo }) {
      /*
        If this id was updated and the update was from undo/redo,
        then update the content of the text editor. This is needed to ensure
        text content is updated after undo/redo while this module id is
        currently the active module. The 'content' doesn't automatically update
        after changes in the app store for the active module to prevent
        losing the cursor position while typing.
      */
      if (updatedModule.id === this.id && isLocalUndoRedo) {
        this.$nextTick(() => {
          if (this.textEditor) {
            this.textEditor.setEditorContent();
          }
        });
      }
    },
    fontFamilyFilter(family) {
      const deOnlyFonts = [
        'font-agrandir',
        'font-sesame-workshop',
        'font-c-rial-black',
        'font-veneer-three',
      ];
      return this.isDeUser || !deOnlyFonts.includes(family.class);
    },
    updateEditorContent() {
      const { textEditor } = this.$refs;
      if (textEditor) {
        textEditor.setEditorContent();
      }
    },
  },
};
</script>

<style lang="stylus">
$sample-response-color = #ba4e9c;

.studio-text-editor {
  height: 100%;
  /*
    Force new block formatting context to ensure that margins
    on child elements will expand this box vertically.
  */
  padding: 0.05px;
  width: 100%;

  &__toolbar {
    &--floating {
      box-shadow: 0px 2px 4px rgba($nebula-color-black, 0.25);
    }

    &--embedded {
      border-radius: 6px;
      box-shadow: none;
      flex-wrap: wrap;
      // Even if the menu bar isn't floating, the tooltips for icons still
      // expect this to be in its own stacking context
      transform: translate(0, 0);
    }
  }

  &__editor {
    &--rounded {
      border-radius: 6px;
    }

    &.studio-text-editor__editor--notes-edit-autosize {
      .ProseMirror {
        /*
          In edit mode, fix the width of notes content to its width
          when the scrollbar is showing. This allows autoresize to size the
          module so that all content fits inside the module regardless if
          a scrollbar is present or not.
        */
        width: 243px;
      }
    }
  }

  &__editor-menu--not-floating {
    z-index: 1;  // Above text content.
  }

  &--placeholder-styles {
    box-shadow: 0px 0px 0px 1px $nebula-color-platform-neutral-400;
  }

  word-tracking(toolbar-align-items) {
    $toolbar-height = 86px;
    .studio-text-editor__word-tracking-toolbar {
      align-items: toolbar-align-items;
      background: $nebula-color-white;
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
      display: flex;
      height: $toolbar-height;
      padding: $nebula-space-1x;
    }

    .studio-text-editor__editor {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      height: "calc(100% - %s)" % $toolbar-height;
    }
  }

  &--word-tracking-nebula {
    word-tracking(flex-end);
  }

  &--word-tracking-mystery {
    word-tracking(flex-start);
  }

  .titan-text-editor {
    .weight-500 {
      font-weight: 500;
    }

    .letter-spacing-0-075 {
      letter-spacing: 0.075em;
    }

    .line-height-150 {
      line-height: 150%;
    }

    .word-spacing-0-3 {
      word-spacing: 0.3em;
    }

    .text-color-sample-response {
      color: $sample-response-color;
      fill: $sample-response-color;
    }
  }
}

.titan-text-editor-toolbar {
  .text-color-swatch-button__text-color--sample-response {
    background-color: $sample-response-color;
  }
}
</style>
